/** Admin UI / API sections that can be granted independently. */
export enum AdminSectionEnum {
    DASHBOARD = 'dashboard',
    CITIES = 'cities',
    QUESTS = 'quests',
    ROUTES = 'routes',
    PEOPLE = 'people',
    ATTRACTIONS = 'attractions',
    ESTABLISHMENTS = 'establishments',
    DISTRICTS = 'districts',
    FOODS = 'foods',
    AUTHORS = 'authors',
    VENUES = 'venues',
    ACTIVITIES = 'activities',
    ORDERS = 'orders',
    PURCHASES = 'purchases',
    REVIEWS = 'reviews',
    TRANSLATIONS = 'translations',
    USERS = 'users',
    QA = 'qa',
}

/** Capabilities on a section (logical actions). */
export enum AdminCapabilityEnum {
    VIEW = 'view',
    /**
     * Stop copy only (routes). Implies view, not edit.
     * Structure, audio, metadata, create and delete stay closed.
     */
    TEXT = 'text',
    EDIT = 'edit',
    DELETE = 'delete',
}

export type AdminSectionCapabilities = Partial<Record<AdminSectionEnum, AdminCapabilityEnum[]>>;

export type AdminPermissions = {
    sections: AdminSectionCapabilities;
    /**
     * Routes a text-only admin may open. Empty = none.
     * Omitted or null = unrestricted (full editors and legacy rows).
     */
    routeIds?: number[] | null;
};

export type AdminSectionDefinition = {
    section: AdminSectionEnum;
    label: string;
    description: string;
    capabilities: AdminCapabilityEnum[];
};

/** Catalog for admin UI permission matrix. */
export const ADMIN_SECTION_CATALOG: AdminSectionDefinition[] = [
    {
        section: AdminSectionEnum.DASHBOARD,
        label: 'Dashboard',
        description: 'Overview and city pipeline summary.',
        capabilities: [AdminCapabilityEnum.VIEW],
    },
    {
        section: AdminSectionEnum.CITIES,
        label: 'Cities',
        description: 'City catalog and locale meta.',
        capabilities: [
            AdminCapabilityEnum.VIEW,
            AdminCapabilityEnum.EDIT,
            AdminCapabilityEnum.DELETE,
        ],
    },
    {
        section: AdminSectionEnum.QUESTS,
        label: 'Quests',
        description: 'City quests — meta, constructor, locales.',
        capabilities: [
            AdminCapabilityEnum.VIEW,
            AdminCapabilityEnum.EDIT,
            AdminCapabilityEnum.DELETE,
        ],
    },
    {
        section: AdminSectionEnum.ROUTES,
        label: 'Routes',
        description: 'Ready walking routes. Text is stop copy on assigned routes only.',
        capabilities: [
            AdminCapabilityEnum.VIEW,
            AdminCapabilityEnum.TEXT,
            AdminCapabilityEnum.EDIT,
            AdminCapabilityEnum.DELETE,
        ],
    },
    {
        section: AdminSectionEnum.PEOPLE,
        label: 'People',
        description: 'Historical people linked to cities.',
        capabilities: [
            AdminCapabilityEnum.VIEW,
            AdminCapabilityEnum.EDIT,
            AdminCapabilityEnum.DELETE,
        ],
    },
    {
        section: AdminSectionEnum.ATTRACTIONS,
        label: 'Attractions',
        description: 'Places / attractions pool.',
        capabilities: [
            AdminCapabilityEnum.VIEW,
            AdminCapabilityEnum.EDIT,
            AdminCapabilityEnum.DELETE,
        ],
    },
    {
        section: AdminSectionEnum.ESTABLISHMENTS,
        label: 'Establishments',
        description: 'Cafes, bars and yards a city guide can recommend.',
        capabilities: [
            AdminCapabilityEnum.VIEW,
            AdminCapabilityEnum.EDIT,
            AdminCapabilityEnum.DELETE,
        ],
    },
    {
        section: AdminSectionEnum.DISTRICTS,
        label: 'Districts',
        description: 'Neighbourhood chapters of a city guide.',
        capabilities: [
            AdminCapabilityEnum.VIEW,
            AdminCapabilityEnum.EDIT,
            AdminCapabilityEnum.DELETE,
        ],
    },
    {
        section: AdminSectionEnum.FOODS,
        label: 'Food',
        description: 'Local food rituals and the places that serve them.',
        capabilities: [
            AdminCapabilityEnum.VIEW,
            AdminCapabilityEnum.EDIT,
            AdminCapabilityEnum.DELETE,
        ],
    },
    {
        section: AdminSectionEnum.AUTHORS,
        label: 'Authors',
        description: 'Portal author accounts.',
        capabilities: [
            AdminCapabilityEnum.VIEW,
            AdminCapabilityEnum.EDIT,
            AdminCapabilityEnum.DELETE,
        ],
    },
    {
        section: AdminSectionEnum.VENUES,
        label: 'Venues',
        description: 'Portal venue accounts.',
        capabilities: [
            AdminCapabilityEnum.VIEW,
            AdminCapabilityEnum.EDIT,
            AdminCapabilityEnum.DELETE,
        ],
    },
    {
        section: AdminSectionEnum.ACTIVITIES,
        label: 'Activities',
        description: 'GetYourGuide tours curated per city.',
        capabilities: [
            AdminCapabilityEnum.VIEW,
            AdminCapabilityEnum.EDIT,
            AdminCapabilityEnum.DELETE,
        ],
    },
    {
        section: AdminSectionEnum.ORDERS,
        label: 'Orders',
        description: 'Quest / product orders.',
        capabilities: [AdminCapabilityEnum.VIEW, AdminCapabilityEnum.EDIT],
    },
    {
        section: AdminSectionEnum.PURCHASES,
        label: 'Purchases',
        description: 'Content purchases — routes, quests, city packs and passes.',
        capabilities: [AdminCapabilityEnum.VIEW],
    },
    {
        section: AdminSectionEnum.REVIEWS,
        label: 'Reviews',
        description: 'Player reviews for quests and ready routes.',
        capabilities: [
            AdminCapabilityEnum.VIEW,
            AdminCapabilityEnum.EDIT,
            AdminCapabilityEnum.DELETE,
        ],
    },
    {
        section: AdminSectionEnum.TRANSLATIONS,
        label: 'Translations',
        description: 'Translation keys and missing strings.',
        capabilities: [AdminCapabilityEnum.VIEW, AdminCapabilityEnum.EDIT],
    },
    {
        section: AdminSectionEnum.USERS,
        label: 'Admins',
        description: 'Create and manage admin accounts and rights.',
        capabilities: [
            AdminCapabilityEnum.VIEW,
            AdminCapabilityEnum.EDIT,
            AdminCapabilityEnum.DELETE,
        ],
    },
    {
        section: AdminSectionEnum.QA,
        label: 'QA',
        description: 'Moderator text-edit quality quiz.',
        capabilities: [AdminCapabilityEnum.VIEW, AdminCapabilityEnum.EDIT],
    },
];

export const emptyAdminPermissions = (): AdminPermissions => ({ sections: {} });

export const fullAdminPermissions = (): AdminPermissions => ({
    sections: Object.fromEntries(
        ADMIN_SECTION_CATALOG.map((row) => [row.section, [...row.capabilities]]),
    ) as AdminSectionCapabilities,
});
